import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Switch,
  Alert,
  TextInput,
  Platform,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as LocalAuthentication from 'expo-local-authentication';
import {
  Ionicons,
  Feather,
  MaterialCommunityIcons,
  MaterialIcons,
  AntDesign,
  FontAwesome5,
} from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';

const ProfileSettings = () => {
  const navigation = useNavigation();
  const [settings, setSettings] = useState({
    notifications: true,
    emailNotifications: true,
    darkMode: false,
    autoPlay: true,
    downloadWifiOnly: true,
    showProgress: true,
    soundEffects: true,
    vibration: true,
  });
  
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricType, setBiometricType] = useState('');
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [loading, setLoading] = useState(false);

  const colors = {
    primary: '#4F46E5',
    secondary: '#10B981',
    danger: '#EF4444',
    warning: '#F97316',
    dark: '#1F2937',
    light: '#F9FAFB',
    white: '#FFFFFF',
    gray: '#6B7280',
    lightGray: '#E5E7EB',
  };

  useEffect(() => {
    loadSettings();
    checkBiometricAvailability();
    loadBiometricStatus();
  }, []);

  const loadSettings = async () => {
    try {
      const savedSettings = await AsyncStorage.getItem('learning_settings');
      if (savedSettings) {
        setSettings(JSON.parse(savedSettings));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const saveSettings = async (newSettings) => {
    try {
      await AsyncStorage.setItem('learning_settings', JSON.stringify(newSettings));
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  };

  const checkBiometricAvailability = async () => {
    try {
      const hasHardware = await LocalAuthentication.hasHardwareAsync();
      const isEnrolled = await LocalAuthentication.isEnrolledAsync();
      const supportedTypes = await LocalAuthentication.supportedAuthenticationTypesAsync();
      
      const available = hasHardware && isEnrolled;
      setBiometricAvailable(available);
      
      if (available && supportedTypes.length > 0) {
        if (supportedTypes.includes(LocalAuthentication.AuthenticationType.FACIAL_RECOGNITION)) {
          setBiometricType('Face ID');
        } else if (supportedTypes.includes(LocalAuthentication.AuthenticationType.FINGERPRINT)) {
          setBiometricType('Touch ID');
        } else {
          setBiometricType('Biometric');
        }
      }
    } catch (error) {
      console.error('Error checking biometric availability:', error);
    }
  };

  const loadBiometricStatus = async () => {
    try {
      const biometricData = await AsyncStorage.getItem('learning_biometric');
      setBiometricEnabled(!!biometricData);
    } catch (error) {
      console.error('Error loading biometric status:', error);
    }
  };

  const handleSettingToggle = async (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    await saveSettings(newSettings);
  };

  const handleBiometricToggle = async (value) => {
    if (!biometricAvailable) {
      Alert.alert('Not Available', 'Biometric authentication is not available on this device');
      return;
    }

    if (value) {
      // Enable biometric
      try {
        const result = await LocalAuthentication.authenticateAsync({
          promptMessage: `Enable ${biometricType} for learning app`,
          cancelLabel: 'Cancel',
          fallbackLabel: 'Use Password',
        });

        if (result.success) {
          // Save biometric data
          const biometricData = {
            enabled: true,
            type: biometricType,
            enabledAt: new Date().toISOString(),
          };
          await AsyncStorage.setItem('learning_biometric', JSON.stringify(biometricData));
          setBiometricEnabled(true);
          Alert.alert('Success', `${biometricType} has been enabled`);
        } else {
          Alert.alert('Cancelled', 'Biometric setup was cancelled');
        }
      } catch (error) {
        console.error('Error enabling biometric:', error);
        Alert.alert('Error', 'Failed to enable biometric authentication');
      }
    } else {
      // Disable biometric
      Alert.alert(
        'Disable Biometric',
        `Are you sure you want to disable ${biometricType}?`,
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Disable',
            style: 'destructive',
            onPress: async () => {
              await AsyncStorage.removeItem('learning_biometric');
              setBiometricEnabled(false);
            },
          },
        ]
      );
    }
  };

  const handleClearCache = () => {
    Alert.alert(
      'Clear Cache',
      'This will remove all cached data including offline courses. Are you sure?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Clear',
          style: 'destructive',
          onPress: async () => {
            try {
              await AsyncStorage.removeItem('learning_cache');
              Alert.alert('Success', 'Cache cleared successfully');
            } catch (error) {
              console.error('Error clearing cache:', error);
              Alert.alert('Error', 'Failed to clear cache');
            }
          },
        },
      ]
    );
  };

  const handleExportData = () => {
    Alert.alert('Export Data', 'Your learning data will be exported to your email.', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Export', onPress: () => {} },
    ]);
  };

  const handleDeleteAccount = () => {
    Alert.alert(
      'Delete Account',
      'This action cannot be undone. All your learning data will be permanently deleted.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            // Implementation for account deletion
          },
        },
      ]
    );
  };

  const handleChangePassword = () => {
    navigation.navigate('ChangePassword');
  };

  const handlePrivacyPolicy = () => {
    // Navigate to privacy policy screen or open webview
    Alert.alert('Privacy Policy', 'Open privacy policy document');
  };

  const handleTermsOfService = () => {
    // Navigate to terms screen or open webview
    Alert.alert('Terms of Service', 'Open terms of service document');
  };

  const settingsSections = [
    {
      title: 'Account',
      icon: 'user',
      color: colors.primary,
      items: [
        {
          id: 'changePassword',
          title: 'Change Password',
          subtitle: 'Update your account password',
          icon: 'lock',
          type: 'navigate',
          onPress: handleChangePassword,
        },
        {
          id: 'emailNotifications',
          title: 'Email Notifications',
          subtitle: 'Receive email updates',
          icon: 'mail',
          type: 'toggle',
          value: settings.emailNotifications,
          onToggle: (value) => handleSettingToggle('emailNotifications', value),
        },
        {
          id: 'exportData',
          title: 'Export Data',
          subtitle: 'Download your learning data',
          icon: 'download',
          type: 'action',
          onPress: handleExportData,
        },
      ],
    },
    {
      title: 'Notifications',
      icon: 'bell',
      color: colors.secondary,
      items: [
        {
          id: 'notifications',
          title: 'Push Notifications',
          subtitle: 'Receive app notifications',
          icon: 'bell',
          type: 'toggle',
          value: settings.notifications,
          onToggle: (value) => handleSettingToggle('notifications', value),
        },
        {
          id: 'courseUpdates',
          title: 'Course Updates',
          subtitle: 'New content and reminders',
          icon: 'book',
          type: 'toggle',
          value: settings.notifications,
          onToggle: (value) => handleSettingToggle('notifications', value),
        },
        {
          id: 'achievementAlerts',
          title: 'Achievement Alerts',
          subtitle: 'Get notified about badges',
          icon: 'award',
          type: 'toggle',
          value: settings.notifications,
          onToggle: (value) => handleSettingToggle('notifications', value),
        },
      ],
    },
    {
      title: 'Learning Preferences',
      icon: 'settings',
      color: colors.warning,
      items: [
        {
          id: 'autoPlay',
          title: 'Auto-play Videos',
          subtitle: 'Automatically play next video',
          icon: 'play-circle',
          type: 'toggle',
          value: settings.autoPlay,
          onToggle: (value) => handleSettingToggle('autoPlay', value),
        },
        {
          id: 'downloadWifiOnly',
          title: 'Wi-Fi Only Downloads',
          subtitle: 'Download courses only on Wi-Fi',
          icon: 'wifi',
          type: 'toggle',
          value: settings.downloadWifiOnly,
          onToggle: (value) => handleSettingToggle('downloadWifiOnly', value),
        },
        {
          id: 'showProgress',
          title: 'Show Progress',
          subtitle: 'Display learning progress',
          icon: 'trending-up',
          type: 'toggle',
          value: settings.showProgress,
          onToggle: (value) => handleSettingToggle('showProgress', value),
        },
        {
          id: 'soundEffects',
          title: 'Sound Effects',
          subtitle: 'Play sounds for interactions',
          icon: 'volume-2',
          type: 'toggle',
          value: settings.soundEffects,
          onToggle: (value) => handleSettingToggle('soundEffects', value),
        },
      ],
    },
    {
      title: 'Security',
      icon: 'shield',
      color: colors.danger,
      items: [
        {
          id: 'biometric',
          title: biometricType || 'Biometric Login',
          subtitle: biometricEnabled ? 'Enabled' : 'Disabled',
          icon: biometricType === 'Face ID' ? 'user-check' : 'lock',
          type: 'toggle',
          value: biometricEnabled,
          onToggle: handleBiometricToggle,
        },
        {
          id: 'clearCache',
          title: 'Clear Cache',
          subtitle: 'Remove temporary files',
          icon: 'trash-2',
          type: 'action',
          onPress: handleClearCache,
        },
        {
          id: 'appLock',
          title: 'App Lock',
          subtitle: 'Require PIN to open app',
          icon: 'lock',
          type: 'toggle',
          value: false,
          onToggle: () => {},
        },
      ],
    },
    {
      title: 'Appearance',
      icon: 'droplet',
      color: colors.primary,
      items: [
        {
          id: 'darkMode',
          title: 'Dark Mode',
          subtitle: 'Use dark theme',
          icon: 'moon',
          type: 'toggle',
          value: settings.darkMode,
          onToggle: (value) => handleSettingToggle('darkMode', value),
        },
        {
          id: 'fontSize',
          title: 'Font Size',
          subtitle: 'Adjust text size',
          icon: 'type',
          type: 'navigate',
          onPress: () => navigation.navigate('FontSizeSettings'),
        },
        {
          id: 'language',
          title: 'Language',
          subtitle: 'English (US)',
          icon: 'globe',
          type: 'navigate',
          onPress: () => navigation.navigate('LanguageSettings'),
        },
      ],
    },
    {
      title: 'About & Support',
      icon: 'info',
      color: colors.gray,
      items: [
        {
          id: 'privacy',
          title: 'Privacy Policy',
          subtitle: 'Read our privacy policy',
          icon: 'shield',
          type: 'action',
          onPress: handlePrivacyPolicy,
        },
        {
          id: 'terms',
          title: 'Terms of Service',
          subtitle: 'Read our terms and conditions',
          icon: 'file-text',
          type: 'action',
          onPress: handleTermsOfService,
        },
        {
          id: 'help',
          title: 'Help & Support',
          subtitle: 'Get help with the app',
          icon: 'help-circle',
          type: 'navigate',
          onPress: () => navigation.navigate('HelpCenter'),
        },
        {
          id: 'rateApp',
          title: 'Rate App',
          subtitle: 'Rate us on the app store',
          icon: 'star',
          type: 'action',
          onPress: () => {},
        },
        {
          id: 'version',
          title: 'App Version',
          subtitle: 'Version 2.1.0',
          icon: 'tag',
          type: 'info',
          onPress: null,
        },
      ],
    },
    {
      title: 'Account Management',
      icon: 'user-x',
      color: colors.danger,
      items: [
        {
          id: 'deleteAccount',
          title: 'Delete Account',
          subtitle: 'Permanently delete your account',
          icon: 'user-x',
          type: 'action',
          danger: true,
          onPress: handleDeleteAccount,
        },
      ],
    },
  ];

  const renderSettingItem = (item) => {
    if (item.type === 'toggle') {
      return (
        <View style={styles.settingItem}>
          <View style={styles.settingItemLeft}>
            <View style={[styles.settingIcon, { backgroundColor: `${colors.primary}20` }]}>
              <Feather name={item.icon} size={18} color={colors.primary} />
            </View>
            <View style={styles.settingText}>
              <Text style={styles.settingTitle}>{item.title}</Text>
              <Text style={styles.settingSubtitle}>{item.subtitle}</Text>
            </View>
          </View>
          <Switch
            value={item.value}
            onValueChange={item.onToggle}
            trackColor={{ false: colors.lightGray, true: colors.primary }}
            thumbColor="#FFFFFF"
          />
        </View>
      );
    }

    return (
      <TouchableOpacity 
        style={styles.settingItem} 
        onPress={item.onPress}
        disabled={!item.onPress}
      >
        <View style={styles.settingItemLeft}>
          <View style={[styles.settingIcon, { 
            backgroundColor: item.danger ? `${colors.danger}20` : `${colors.primary}20` 
          }]}>
            <Feather 
              name={item.icon} 
              size={18} 
              color={item.danger ? colors.danger : colors.primary} 
            />
          </View>
          <View style={styles.settingText}>
            <Text style={[styles.settingTitle, item.danger && styles.dangerText]}>
              {item.title}
            </Text>
            <Text style={styles.settingSubtitle}>{item.subtitle}</Text>
          </View>
        </View>
        {item.onPress && (
          <Feather name="chevron-right" size={20} color={colors.gray} />
        )}
      </TouchableOpacity>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="dark-content" backgroundColor={colors.white} />
      
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerTop}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
            <Ionicons name="arrow-back" size={24} color={colors.dark} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Settings</Text>
          <View style={styles.headerRight} />
        </View>
      </View>

      <ScrollView showsVerticalScrollIndicator={false} style={styles.scrollView}>
        {settingsSections.map((section, sectionIndex) => (
          <View key={sectionIndex} style={styles.section}>
            <View style={styles.sectionHeader}>
              <View style={[styles.sectionIcon, { backgroundColor: section.color }]}>
                <Feather name={section.icon} size={18} color={colors.white} />
              </View>
              <Text style={styles.sectionTitle}>{section.title}</Text>
            </View>
            
            <View style={styles.sectionContent}>
              {section.items.map((item, itemIndex) => (
                <View key={item.id}>
                  {renderSettingItem(item)}
                  {itemIndex < section.items.length - 1 && <View style={styles.separator} />}
                </View>
              ))}
            </View>
          </View>
        ))}
        
        <View style={styles.appInfo}>
          <Text style={styles.appName}>JobEng Learning</Text>
          <Text style={styles.appVersion}>Version 2.1.0 • Build 210</Text>
          <Text style={styles.appCopyright}>© 2024 JobEng AI. All rights reserved.</Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  header: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 20,
    paddingTop: 10,
    paddingBottom: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
  },
  headerRight: {
    width: 40,
  },
  scrollView: {
    flex: 1,
  },
  section: {
    marginTop: 20,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    marginBottom: 12,
  },
  sectionIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
  },
  sectionContent: {
    backgroundColor: '#FFFFFF',
    marginHorizontal: 20,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  settingItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
  },
  settingItemLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  settingIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  settingText: {
    flex: 1,
  },
  settingTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#1F2937',
    marginBottom: 2,
  },
  settingSubtitle: {
    fontSize: 14,
    color: '#6B7280',
  },
  dangerText: {
    color: '#EF4444',
  },
  separator: {
    height: 1,
    backgroundColor: '#F3F4F6',
    marginHorizontal: 16,
  },
  appInfo: {
    alignItems: 'center',
    padding: 30,
    marginBottom: 20,
  },
  appName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  appVersion: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 4,
  },
  appCopyright: {
    fontSize: 12,
    color: '#9CA3AF',
    textAlign: 'center',
  },
});

export default ProfileSettings;